import { type KeyboardEvent, type ReactNode, useId, useRef } from "react";

/**
 * A row of tabs over one panel. Controlled: the parent holds `active` and
 * decides what `children` is. Only the active tab is in the tab order; the
 * arrow keys, Home and End move between tabs, and moving activates (the tab
 * takes focus and `onChange` is called).
 */
export function Tabs(props: {
  tabs: { id: string; label: string }[];
  active: string;
  onChange(id: string): void;
  /** The active panel's content. */
  children: ReactNode;
}): JSX.Element {
  const { tabs, active, onChange, children } = props;
  const p = useId();
  const list = useRef<HTMLDivElement>(null);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const at = tabs.findIndex((tab) => tab.id === active);
    const last = tabs.length - 1;
    let to: number;
    if (event.key === "ArrowRight") to = at >= last ? 0 : at + 1;
    else if (event.key === "ArrowLeft") to = at <= 0 ? last : at - 1;
    else if (event.key === "Home") to = 0;
    else if (event.key === "End") to = last;
    else return;
    event.preventDefault();
    const next = tabs[to];
    if (!next) return;
    list.current
      ?.querySelector<HTMLElement>(`[id="${p}-tab-${next.id}"]`)
      ?.focus();
    onChange(next.id);
  };

  return (
    <div className="tb-tabs">
      <div role="tablist" ref={list} onKeyDown={onKeyDown}>
        {tabs.map((tab) => {
          const selected = tab.id === active;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={`${p}-tab-${tab.id}`}
              aria-selected={selected}
              aria-controls={`${p}-panel`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={`${p}-panel`}
        aria-labelledby={`${p}-tab-${active}`}
        tabIndex={0}
      >
        {children}
      </div>
    </div>
  );
}
